import { useState, useEffect, useCallback, useRef } from 'react';
import type { ImageInfo, ImageStorageSettings } from '../types';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useEscapeKey } from '../hooks/useEscapeKey';

interface ImagesPanelProps {
  project_id?: number;
  category_id?: number;
  topic_id?: number;
  page_id?: number;
  label?: string;
}

// Mirror the backend's _candidate_prefixes so we can tell when an image_url
// lives in one of the user's own buckets (→ offer "also delete the file").
function storagePrefixes(settings: ImageStorageSettings[]): string[] {
  const prefixes: string[] = [];
  for (const s of settings) {
    if (s.public_base_url) prefixes.push(s.public_base_url.replace(/\/+$/, '') + '/');
    if (s.endpoint_url) prefixes.push(s.endpoint_url.replace(/\/+$/, '') + `/${s.bucket}/`);
    const region = s.region || 'us-east-1';
    prefixes.push(`https://${s.bucket}.s3.${region}.amazonaws.com/`);
  }
  return prefixes;
}

export default function ImagesPanel({
  project_id,
  category_id,
  topic_id,
  page_id,
  label = 'Images',
}: ImagesPanelProps) {
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [prefixes, setPrefixes] = useState<string[]>([]);
  const [hasStorage, setHasStorage] = useState(false);
  const [optimizeOn, setOptimizeOn] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);

  // Delete confirmation modal target.
  const [deleteTarget, setDeleteTarget] = useState<ImageInfo | null>(null);
  const [alsoDeleteFile, setAlsoDeleteFile] = useState(false);

  // Multi-select for bulk delete.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAlsoDeleteFile, setBulkAlsoDeleteFile] = useState(false);

  // Track whether Shift is held so we can "arm" the trash icon red — a visual
  // cue that Shift+Delete will also purge the file from the bucket.
  const [shiftHeld, setShiftHeld] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const filters = { project_id, category_id, topic_id, page_id };

  // Live updates: when a generate/optimize job finishes for an image in this
  // panel, refetch so the renditions (and final URL) appear without a refresh.
  const { subscribe } = useWebSocket();
  const imageIdsRef = useRef<Set<number>>(new Set());

  // <Esc> closes the delete-confirmation and bulk-delete modals.
  useEscapeKey(deleteTarget !== null, () => setDeleteTarget(null));
  useEscapeKey(bulkOpen, () => setBulkOpen(false));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(e.type === 'keydown');
    };
    const onBlur = () => setShiftHeld(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getImages(filters);
      setImages(data);
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to fetch images:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project_id, category_id, topic_id, page_id]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Keep a ref of the currently-shown image ids so the websocket handler can
  // cheaply tell whether an event is for an image in THIS panel's scope.
  useEffect(() => {
    imageIdsRef.current = new Set(images.map((i) => i.id));
  }, [images]);

  useEffect(() => {
    const unsub = subscribe((event) => {
      if (
        (event.event_type === 'image_optimized' ||
          event.event_type === 'image_generated' ||
          event.event_type === 'image_metadata_extracted') &&
        imageIdsRef.current.has(event.image_id)
      ) {
        fetchImages();
      } else if (
        // A NEW IMAGE has an id this panel has never seen, so the check above
        // let it through unnoticed: a page's generated image never appeared.
        event.event_type === 'image_generated' &&
        event.status === 'completed' &&
        page_id != null &&
        event.page_id === page_id
      ) {
        fetchImages();
      }
    });
    return unsub;
  }, [subscribe, fetchImages, page_id]);

  // Storage config drives both upload availability and the delete-from-storage offer.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.getImageStorageSettings();
        const enabled = (res.items || []).filter((s) => s.is_enabled);
        setHasStorage(enabled.length > 0);
        setOptimizeOn(enabled.some((s) => s.optimize_uploads));
        setPrefixes(storagePrefixes(enabled));
      } catch {
        /* storage is optional */
      }
    })();
  }, []);

  const urlInBucket = (url: string | null) => !!url && prefixes.some((p) => url.startsWith(p));

  const handleCreate = async (data: { title: string; alt_text: string; description: string; image_url: string }) => {
    setError('');
    try {
      await api.createImage({ ...data, ...filters });
      setShowAdd(false);
      await fetchImages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create image');
    }
  };

  const handleUpload = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (!images.length) return;
    setError('');
    setUploading(true);
    setUploadProgress({ done: 0, total: images.length });
    const errors: string[] = [];
    for (let i = 0; i < images.length; i++) {
      try {
        await api.uploadImage(images[i], filters);
      } catch (err) {
        errors.push(images[i].name + ': ' + (err instanceof Error ? err.message : 'failed'));
      }
      setUploadProgress({ done: i + 1, total: images.length });
    }
    setUploading(false);
    setUploadProgress(null);
    if (errors.length) setError(`Some uploads failed - ${errors.join('; ')}`);
    await fetchImages();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(Array.from(e.dataTransfer.files || []));
  };

  // Shift+Delete skips the dialog but states clearly that it removes both.
  const requestDelete = (img: ImageInfo, shiftPressed: boolean) => {
    if (shiftPressed) {
      const inBucket = urlInBucket(img.image_url);
      const msg = inBucket
        ? 'Shift-delete: this removes the image reference AND deletes the file from your storage bucket. Continue?'
        : 'Delete this image reference? Its URL is not in a configured bucket, so no file will be deleted.';
      if (window.confirm(msg)) doDelete(img, inBucket);
      return;
    }
    setAlsoDeleteFile(false);
    setDeleteTarget(img);
  };

  const doDelete = async (img: ImageInfo, deleteFile: boolean) => {
    try {
      await api.deleteImage(img.id, deleteFile);
      setImages((prev) => prev.filter((i) => i.id !== img.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  // ── Multi-select / bulk delete ──
  const toggleSelect = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectAll = () => setSelected(new Set(images.map((i) => i.id)));
  const clearSelection = () => setSelected(new Set());

  const selectedImages = images.filter((i) => selected.has(i.id));
  const selectedInBucket = selectedImages.filter((i) => urlInBucket(i.image_url)).length;

  const requestBulkDelete = (shiftPressed: boolean) => {
    if (selected.size === 0) return;
    if (shiftPressed) {
      const msg =
        selectedInBucket > 0
          ? `Shift-delete ${selected.size} image(s): removes the references AND purges ${selectedInBucket} file(s) from your bucket(s). Continue?`
          : `Delete ${selected.size} image reference(s)? None are in a configured bucket, so no files are deleted.`;
      if (window.confirm(msg)) doBulkDelete(true);
      return;
    }
    setBulkAlsoDeleteFile(false);
    setBulkOpen(true);
  };

  const doBulkDelete = async (deleteFile: boolean) => {
    const ids = Array.from(selected);
    // Backend only purges files whose URL is in a bucket, so passing
    // delete_file for the whole batch is safe for mixed selections.
    const results = await Promise.allSettled(
      ids.map((id) => api.deleteImage(id, deleteFile)),
    );
    const failed = new Set(
      ids.filter((_, idx) => results[idx].status === 'rejected'),
    );
    setImages((prev) => prev.filter((i) => failed.has(i.id) || !selected.has(i.id)));
    setSelected(failed);
    setBulkOpen(false);
    if (failed.size) setError(`Failed to delete ${failed.size} image(s).`);
  };

  const needsUrl = images.filter((img) => !img.image_url && !img.generation_error && img.source !== 'generated').length;

  return (
    <div>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
        {images.length > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
            {images.length}
          </span>
        )}
        {needsUrl > 0 && (
          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
            {needsUrl} needs URL
          </span>
        )}
      </div>

      <div
        className={`space-y-3 border rounded-lg p-4 ${dragOver ? 'border-blue-400 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700 dark:hover:text-red-300">&times;</button>
          </div>
        )}

        {/* Add / upload actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {!showAdd && (
            <button
              onClick={() => setShowAdd(true)}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/40"
            >
              + Add by URL
            </button>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={!hasStorage || uploading}
            title={hasStorage ? 'Upload image files to your bucket' : 'Configure Image Storage (Settings) to enable uploads'}
            className="px-3 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50"
          >
            {uploading
              ? uploadProgress
                ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                : 'Uploading…'
              : '⬆ Upload files'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              handleUpload(Array.from(e.target.files || []));
              e.target.value = '';
            }}
          />
          {hasStorage && (
            <span className="text-xs text-gray-400 dark:text-gray-500">or drag &amp; drop images here</span>
          )}
        </div>

        {showAdd && (
          <AddImageForm onSubmit={handleCreate} onCancel={() => setShowAdd(false)} />
        )}

        {/* Bulk-select action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 flex-wrap bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">{selected.size} selected</span>
            <button onClick={selectAll} className="text-blue-600 dark:text-blue-400 hover:underline text-xs">
              Select all ({images.length})
            </button>
            <button onClick={clearSelection} className="text-gray-500 dark:text-gray-400 hover:underline text-xs">
              Clear
            </button>
            <div className="flex-1" />
            <button
              onClick={(e) => requestBulkDelete(e.shiftKey)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                shiftHeld && selectedInBucket > 0
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/40'
              }`}
              title={
                shiftHeld && selectedInBucket > 0
                  ? 'Shift+Delete: also purges files from the bucket'
                  : 'Delete selected (Shift+Delete also removes stored files)'
              }
            >
              Delete selected
            </button>
          </div>
        )}

        {/* Images list */}
        {loading ? (
          <div className="text-sm text-gray-400 dark:text-gray-500 animate-pulse">Loading...</div>
        ) : images.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">No images yet</p>
        ) : (
          <div className="space-y-2">
            {images.map((img) => (
              <ImageRow
                key={img.id}
                image={img}
                onDeleteRequest={requestDelete}
                purgeArmed={shiftHeld && urlInBucket(img.image_url)}
                optimizeOn={optimizeOn}
                selected={selected.has(img.id)}
                onToggleSelect={() => toggleSelect(img.id)}
                onUpdate={(updated) =>
                  setImages((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteTarget && (
        <DeleteModal
          image={deleteTarget}
          inBucket={urlInBucket(deleteTarget.image_url)}
          alsoDeleteFile={alsoDeleteFile}
          setAlsoDeleteFile={setAlsoDeleteFile}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => doDelete(deleteTarget, alsoDeleteFile)}
        />
      )}

      {/* Bulk delete confirmation */}
      {bulkOpen && (
        <BulkDeleteModal
          count={selected.size}
          inBucketCount={selectedInBucket}
          alsoDeleteFile={bulkAlsoDeleteFile}
          setAlsoDeleteFile={setBulkAlsoDeleteFile}
          onCancel={() => setBulkOpen(false)}
          onConfirm={() => doBulkDelete(bulkAlsoDeleteFile)}
        />
      )}
    </div>
  );
}

// ─── Bulk delete confirmation modal ──────────────────────────────────────────

function BulkDeleteModal({
  count,
  inBucketCount,
  alsoDeleteFile,
  setAlsoDeleteFile,
  onCancel,
  onConfirm,
}: {
  count: number;
  inBucketCount: number;
  alsoDeleteFile: boolean;
  setAlsoDeleteFile: (v: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Delete {count} image{count === 1 ? '' : 's'}?
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">This removes the selected image references.</p>
        {inBucketCount > 0 ? (
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={alsoDeleteFile}
              onChange={(e) => setAlsoDeleteFile(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 focus:ring-red-500"
            />
            <span className="text-gray-700 dark:text-gray-300">
              Also delete files from storage - purges {inBucketCount} file{inBucketCount === 1 ? '' : 's'} (can't be undone)
            </span>
          </label>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            None of the selected images are in a configured bucket, so only references are removed.
          </p>
        )}
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
          Tip: Shift+click “Delete selected” to remove references and files at once.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700">
            {alsoDeleteFile ? `Delete ${count} + files` : `Delete ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete confirmation modal ───────────────────────────────────────────────

function DeleteModal({
  image,
  inBucket,
  alsoDeleteFile,
  setAlsoDeleteFile,
  onCancel,
  onConfirm,
}: {
  image: ImageInfo;
  inBucket: boolean;
  alsoDeleteFile: boolean;
  setAlsoDeleteFile: (v: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Delete “{image.title || image.placeholder_key || 'image'}”?
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          This removes the image reference{(image.used_on_pages ?? 0) > 0 ? ` (used on ${image.used_on_pages} page${image.used_on_pages === 1 ? '' : 's'})` : ''}.
        </p>
        {inBucket ? (
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={alsoDeleteFile}
              onChange={(e) => setAlsoDeleteFile(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 focus:ring-red-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Also delete the file from storage (can't be undone)</span>
          </label>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            The file isn't in a configured bucket, so only the reference is removed.
          </p>
        )}
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
          Tip: Shift+Delete on the trash icon deletes the reference and the file at once.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700">
            {alsoDeleteFile ? 'Delete reference + file' : 'Delete reference'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Image Form ──────────────────────────────────────────────────────────

function AddImageForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: { title: string; alt_text: string; description: string; image_url: string }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [altText, setAltText] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      />
      <input
        type="text"
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        placeholder="Alt text"
        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (what this image should show)"
        rows={2}
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      />
      <input
        type="url"
        value={imageUrl}
        onChange={(e) => setImageUrl(e.target.value)}
        placeholder="Image URL (optional - can add later)"
        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200">
          Cancel
        </button>
        <button
          onClick={() => onSubmit({ title, alt_text: altText, description, image_url: imageUrl })}
          disabled={!title.trim()}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          Add Image
        </button>
      </div>
    </div>
  );
}

// ─── Image Row with inline editing ──────────────────────────────────────────

function ImageRow({
  image,
  onDeleteRequest,
  purgeArmed = false,
  optimizeOn = false,
  selected = false,
  onToggleSelect,
  onUpdate,
}: {
  image: ImageInfo;
  onDeleteRequest: (image: ImageInfo, shiftPressed: boolean) => void;
  purgeArmed?: boolean;
  optimizeOn?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  onUpdate: (updated: ImageInfo) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(image.title || '');
  const [altText, setAltText] = useState(image.alt_text || '');
  const [description, setDescription] = useState(image.description || '');
  const [imageUrl, setImageUrl] = useState(image.image_url || '');
  const [placeholderKey, setPlaceholderKey] = useState(image.placeholder_key || '');

  const hasUrl = !!image.image_url;
  const isGenerated = image.source === 'generated';
  const generating = isGenerated && !hasUrl && !image.generation_error;
  const failed = !!image.generation_error;
  const usedOn = image.used_on_pages ?? 0;

  const variants = image.variants ?? [];
  const renditions = variants.filter((v) => v.label !== 'original'); // resized ones
  // Uploaded, optimization enabled, but no resized renditions stored yet.
  const optimizing = optimizeOn && image.source === 'user' && hasUrl && renditions.length === 0;
  const fmtBytes = (n: number | null) =>
    n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.updateImage(image.id, {
        title,
        alt_text: altText,
        description,
        image_url: imageUrl,
        placeholder_key: placeholderKey,
      });
      onUpdate(updated);
      setEditing(false);
    } catch (err) {
      console.error('Failed to update image:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-2.5">
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            className="mr-2 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 flex-shrink-0"
            title="Select for bulk actions"
          />
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 min-w-0 flex items-start gap-2 text-left"
        >
          <svg
            className={`w-3.5 h-3.5 mt-0.5 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {image.title || 'Untitled image'}
              </span>
              {failed ? (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                  Failed
                </span>
              ) : generating ? (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 animate-pulse">
                  Generating…
                </span>
              ) : hasUrl ? (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                  Ready
                </span>
              ) : (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                  Needs URL
                </span>
              )}
              {isGenerated && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                  AI
                </span>
              )}
              {image.source === 'discovery' && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                  Discovery
                </span>
              )}
              {optimizing && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 animate-pulse">
                  Optimizing…
                </span>
              )}
              {renditions.length > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300">
                  {renditions.length} sizes
                </span>
              )}
              {usedOn > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  used on {usedOn} page{usedOn === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {image.alt_text && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{image.alt_text}</p>
            )}
          </div>
        </button>
        <button
          onClick={(e) => onDeleteRequest(image, e.shiftKey)}
          className={`p-1.5 rounded flex-shrink-0 transition-colors ${
            purgeArmed
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
          }`}
          title={
            purgeArmed
              ? 'Shift+Delete: removes the reference AND purges the file from the bucket'
              : 'Delete image (Shift+Delete also removes the stored file)'
          }
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 space-y-2">
          {failed && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-2 py-1">
              Generation failed: {image.generation_error}
            </div>
          )}
          {editing ? (
            <>
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md"
              />
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Alt Text</label>
              <input
                type="text"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md"
              />
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md"
              />
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Image URL</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Paste image URL here"
                className={`w-full px-2 py-1 text-sm border rounded-md ${
                  !imageUrl ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Placeholder Key</label>
              <input
                type="text"
                value={placeholderKey}
                onChange={(e) => setPlaceholderKey(e.target.value)}
                placeholder="e.g. brake-pads-hero"
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md font-mono"
              />
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEditing(false)} className="px-2.5 py-1 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200">
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          ) : (
            <>
              {image.image_url && (
                <img
                  src={image.image_url}
                  alt={image.alt_text || ''}
                  className="max-h-40 rounded border border-gray-200 dark:border-gray-700 object-contain"
                />
              )}
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <span className="text-gray-400 dark:text-gray-500">Title</span>
                <span className="text-gray-700 dark:text-gray-300">{image.title || '-'}</span>
                <span className="text-gray-400 dark:text-gray-500">Alt text</span>
                <span className="text-gray-700 dark:text-gray-300">{image.alt_text || '-'}</span>
                <span className="text-gray-400 dark:text-gray-500">Description</span>
                <span className="text-gray-700 dark:text-gray-300">{image.description || '-'}</span>
                <span className="text-gray-400 dark:text-gray-500">URL</span>
                {image.image_url ? (
                  <a href={image.image_url} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 truncate hover:underline">
                    {image.image_url}
                  </a>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400 font-medium">
                    {generating ? 'Generating…' : 'Not set - click Edit to add'}
                  </span>
                )}
                {image.placeholder_key && (
                  <>
                    <span className="text-gray-400 dark:text-gray-500">Placeholder</span>
                    <code className="text-gray-700 dark:text-gray-300 text-[11px] bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{'{{image:' + image.placeholder_key + '}}'}</code>
                  </>
                )}
              </div>

              {/* Nested renditions */}
              {(variants.length > 0 || optimizing) && (
                <div className="mt-1 border-t border-gray-200 dark:border-gray-700 pt-2">
                  <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                    Renditions {optimizing && <span className="text-sky-600 dark:text-sky-400">(optimizing…)</span>}
                  </div>
                  <ul className="space-y-0.5">
                    {variants
                      .slice()
                      .sort((a, b) => a.width - b.width)
                      .map((v) => (
                        <li key={v.id} className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
                          <span className="font-mono text-gray-500 dark:text-gray-400 w-16">
                            {v.label === 'original' ? 'original' : `${v.width}px`}
                          </span>
                          <span className="text-gray-400 dark:text-gray-500">
                            {v.width ? `${v.width}×${v.height ?? '?'}` : ''} {fmtBytes(v.file_size)}
                          </span>
                          {v.is_primary && (
                            <span className="px-1 py-0.5 text-[9px] rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                              primary
                            </span>
                          )}
                          <a href={v.image_url} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 truncate hover:underline">
                            open
                          </a>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end pt-1">
                <button
                  onClick={() => setEditing(true)}
                  className="px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/40"
                >
                  Edit
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
